import "amazon-connect-streams";
import type { JsonValue, LiteralUnion, Tagged } from "type-fest";

import type { MessageSender } from "@/sdk/agent/chat/types";

/**
 * NOTE: Amazon Connect's native types (like connect.ChatEventData) come from the
 * "amazon-connect-streams" library. Those are separate from our custom types below.
 *
 * Amazon Connect provides generic message containers with a Content field (string).
 * We use that Content field to embed our own standardized JSON message structure.
 */

/**
 * Message content type. Currently only "text" is supported, but using LiteralUnion
 * to signal that other values may be received in the future (e.g., "image", "markdown", "video").
 * Forces defensive handling while providing "text" autocomplete.
 */
type MessageContentType = LiteralUnion<"text" | "salesDisclosure", string>;

/**
 * Encryption metadata format for Amazon Connect chat message payloads.
 * This is Connect-specific - the shared encryption module uses different types.
 * Used when creating/sending encrypted messages via Connect's message format.
 *
 * TODO: Move EncryptionDataFields, BaseEncryptedData, and related encryption message processing logic
 * to a shared location (e.g., @/sdk/agent/chat/types.ts) since these structures may be shared
 * across different chat providers (Connect, Twilio, etc.) rather than being Connect-specific.
 */
export interface EncryptionDataFields {
    encrypted: boolean;
    IV: string;
    encryptedDataKey: string;
}

/** Wire format for encrypted message payloads. */
type BaseEncryptedData = EncryptionDataFields;

/**
 * Expert Workspace chat message payload structure.
 *
 * Our custom JSON structure embedded in Amazon Connect's `connect.ChatEventData.Content` field
 * (as a JSON string with ContentType: "application/json").
 *
 * **Important**: This is NOT Amazon Connect's native format. We use their generic Content field
 * to pass our standardized message structure between backend (chat service) and frontend.
 *
 * @see {@link ChatMessageJsonString} for the JSON-stringified version
 */
export interface ChatMessagePayload {
    /** Unique identifier for the message author (typically a userId). For experts, this is the employee ID. */
    author: string;
    /** The actual message text (may be encrypted) */
    body: string;
    /** Source application (e.g., "simplr", "expert-workspace") */
    sourceApp: string;
    /** Type of sender - from the shared chat SDK's MessageSender type */
    senderType: MessageSender;
    /** Display name of the sender (optional, but we may start using this in the future) */
    senderName?: string;
    /** Message type (currently only "text" supported) */
    type: MessageContentType;
    /** Partner/client identifier */
    partner: string;
    /** Topic of the conversation */
    topic: string;
    /** Language of the conversation */
    language: string;
    /** Metadata for the expert */
    expertMetadata?: {
        employeeId: string;
        displayName: string;
        ccaasExpertId: string;
    };
    /** Additional metadata for the message */
    metadata?: {
        externalMessageId?: string;
        externalMessageTimestamp?: string;
        serverReceivedTimestamp?: string;
    } & Record<string, JsonValue>;
    /** if message is encrypted */
    encrypted?: boolean;
    /** Initialization vector for the encryption */
    IV?: string;
    /** Encryption key for the message */
    encryptedDataKey?: string;
    /** Sales disclosure fields */
    triggeredComponent?: string;
    amountPerMonth?: string;
    programDisplayName?: string;
    programDescription?: string;
    programSku?: string;
    callToAction?: string;
}

/**
 * Tagged type for JSON string containing serialized ChatMessagePayload.
 * Used in Amazon Connect's `ChatEventData.Content` field.
 *
 * @example
 * const content: ChatMessageJsonString = JSON.stringify(payload) as ChatMessageJsonString;
 * const parsed: ChatMessagePayload = JSON.parse(content);
 *
 * @see {@link ChatMessagePayload}
 */
export type ChatMessageJsonString = Tagged<string, "ChatMessageJsonString">;

/**
 * Normalized chat message structure used internally by the Amazon Connect Chat SDK.
 */
export type ChatConnectMessage = {
    id: string;
    content: string;
    timestamp: Date;
    direction: "inbound" | "outbound";
    from: string;
    participantRole?: "AGENT" | "CUSTOMER" | "SYSTEM";
    contentType?: string;
};

/**
 * All Known Amazon Connect chat event content types.
 * @see {@link https://github.com/amazon-connect/amazon-connect-chatjs}
 */
export const CHAT_CONTENT_TYPES = {
    // #region Receipt and typing indicators — actively used for UI state
    TYPING: "application/vnd.amazonaws.connect.event.typing",
    MESSAGE_READ: "application/vnd.amazonaws.connect.event.message.read",
    MESSAGE_DELIVERED: "application/vnd.amazonaws.connect.event.message.delivered",
    // #endregion

    // #region Participant lifecycle — used to derive join/leave times for session timing. Idle times are not used in our contact flows.
    PARTICIPANT_JOINED: "application/vnd.amazonaws.connect.event.participant.joined",
    PARTICIPANT_LEFT: "application/vnd.amazonaws.connect.event.participant.left",
    /** Fired when a participant goes idle (no activity for a configured duration). */
    PARTICIPANT_IDLE: "application/vnd.amazonaws.connect.event.participant.idle",
    /** Fired when an idle participant returns to the chat. Not currently acted on. */
    PARTICIPANT_RETURNED: "application/vnd.amazonaws.connect.event.participant.returned",
    /** Fired when a participant is invited to join (e.g. supervisor barge-in).  */
    PARTICIPANT_INVITED: "application/vnd.amazonaws.connect.event.participant.invited",
    /** Fired when a participant is auto-disconnected due to inactivity timeout. */
    AUTO_DISCONNECTION: "application/vnd.amazonaws.connect.event.participant.autodisconnection",
    // #endregion

    // #region Connection lifecycle — infrastructure events
    /** Handshake acknowledgement sent by the server when the WebSocket connection is established. */
    CONNECTION_ACKNOWLEDGED: "application/vnd.amazonaws.connect.event.connection.acknowledged",
    /** Fired when a chat session ends. Cleanup is driven by the Streams contact lifecycle, not this event. */
    CHAT_ENDED: "application/vnd.amazonaws.connect.event.chat.ended",
    // #endregion

    // #region Transfer events — contact transfers are not used via this mechanism in our flows.
    /** Fired when a contact transfer completes successfully. */
    TRANSFER_SUCCEEDED: "application/vnd.amazonaws.connect.event.transfer.succeeded",
    /** Fired when a contact transfer fails. */
    TRANSFER_FAILED: "application/vnd.amazonaws.connect.event.transfer.failed",
    // #endregion

    // #region Persistent chat — not currently used in our contact flows
    /** Fired when a returning customer's previous conversation is rehydrated into the transcript. */
    CHAT_REHYDRATED: "application/vnd.amazonaws.connect.event.chat.rehydrated",
    // #endregion
} as const satisfies Record<string, connect.ChatEventContentType>;

/**
 * Participant events we can see in the Amazon Connect chat transcript.
 * Used to track session timing, especially agentJoinTime->assignedAt mapping.
 */
export interface ConnectTranscriptEvents {
    agentJoinTime?: Date;
    customerJoinTime?: Date;
    agentLeaveTime?: Date;
    customerLeaveTime?: Date;
}

export interface ContactAttribute {
    value: string;
    name: string;
}

export type EncryptedContactAttribute = ContactAttribute & {
    encrypted?: string;
    encryption?: BaseEncryptedData;
};

export interface ChatContactAttributes {
    mdn?: ContactAttribute | EncryptedContactAttribute;
    partner?: ContactAttribute;
    language?: ContactAttribute;
    channel?: ContactAttribute;
    sourceApp?: ContactAttribute;
    topic?: ContactAttribute;
    targetQueue?: ContactAttribute;
    requestRouting?: ContactAttribute;
    firstName?: ContactAttribute | EncryptedContactAttribute;
    problemStatement?: ContactAttribute | EncryptedContactAttribute;
    /** Original chat ID for transferred/continued chat sessions */
    originalChatId?: ContactAttribute;
    /** Encryption context for contact attributes (mdn, firstName, problemStatement). Set by backend, not used for messages. */
    dataKeyContext?: ContactAttribute;
    [key: string]: ContactAttribute | EncryptedContactAttribute | undefined;
}

/**
 * The contact attribute keys that are required for a chat contact to be considered valid.
 * TODO: add more required fields as we can safely assume they are present
 */
export const REQUIRED_CONTACT_ATTRIBUTES = ["sourceApp", "topic", "language", "partner"] as const;

/**
 * Represents `ChatContactAttributes` after validation, guaranteeing required fields
 * are present and have a `{ value: string }`. For use with type guards.
 */
export type ValidatedChatContactAttributes = ChatContactAttributes & {
    [K in (typeof REQUIRED_CONTACT_ATTRIBUTES)[number]]: { value: string };
};

export interface RelatedChatResponse {
    chatId: string;
    closeReason?: string;
    originalChatId?: string;
}

export interface NeglectionResponse {
    startTime?: number;
    endTime?: number;
    durationSeconds?: number;
    warningCount?: number;
}

export interface AutopilotResponse {
    startTime?: number;
    endTime?: number;
    durationSeconds?: number;
    warningCount?: number;
}

export interface ChatMetadataResponse {
    chatId: string;
    neglection?: NeglectionResponse;
    autopilot?: AutopilotResponse;
}

export interface ChatMetadata {
    ccaasInstance: string;
    chatId: string;
    originalChatId?: string;
    startTime: number;
    chatProvider: string;
    partner: string;
    topic: string;
    language: string;
    sourceApp: string;
    dataKeyContext: string;
    exwoSessionId?: string;
    closeReason?: string;
    endTime?: number;
    requestRouting?: boolean;
    queueName?: string;
}
